import { ReactNode } from 'react'

import type { RootStateType } from '@store/store'

import { FileType, ProjectType } from '@adUtilities/constants/common'
import { PropertyCompare } from '@adUtilities/types'
import { UnitFilterOptions } from '@adUtilities/types/apartment'
import { MinMaxInterface } from '@adUtilities/types/common'
import { FilterOptionsConfigurations } from '@adUtilities/types/houseAndLand'

export type RootStateTypeExtra = RootStateType

export enum HouseAndLandPackageOptionType {
  FloorplanOption = 'floorplanOptions',
  Facade = 'facades',
  FacadeColor = 'facadeColors',
  InternalTheme = 'internalThemes',
  InternalColorScheme = 'internalColorSchemes',
}

export enum InteractivePlanType {
  AreaView = 'areaView',
  Blocks = 'blocks',
  Floorplan = 'floorplan',
  Precinct = 'precinct',
  Stages = 'stages',
}

export enum ShortlistPropertyType {
  Lot = 'lot',
  Unit = 'unit',
}

export enum BackgroundType {
  Image = 'image',
  Color = 'color',
  None = 'none',
}

export type StatusLabels = Record<string, string>

export type StatusColors = Record<string, string>

export interface PriceRange {
  max: Array<number>
  min: Array<number>
}

export interface DropDownOption {
  label: ReactNode
  value: string
}

export interface LotFilterConfiguration {
  frontage: MinMaxInterface
  area: MinMaxInterface
  aspects: Array<string>
  propertyTypes: Array<string>
  price: MinMaxInterface
}

export interface ExtendedUnitFilterOptions extends UnitFilterOptions {
  anyLevel: boolean
  apply: boolean
  levelsShowLabels: boolean
  unitsShowLabels: boolean
}

export interface LotFilterInterface {
  configurations: FilterOptionsConfigurations
  builder: Array<string>
  storey: Array<number>
  packages: Array<string>
  lotConfiguration: LotFilterConfiguration
  showOnlyAvailable: boolean
  anyStage: boolean
  apply: boolean
}

export interface HouseAndLandConfigurationInterface {
  lotFrontage?: Array<number>
  lotArea?: Array<number>
  builder?: Array<string>
  storey?: Array<{ label: string; value: number }>
  packageFilter?: boolean
}

export interface SnapDataInterface {
  views: Array<string>
  levels: Array<{ key: any; value: any }>
}

export interface SnaploaderInterface {
  data: SnapDataInterface
  activeView: string
  activeBlock: string
  rotateCamera: boolean
  isLoaded: boolean
}

export interface EnvisionVRInterface {
  isLoaded: boolean
  buildings: {
    data: Array<string>
  }
  levels: {
    data: Array<string>
  }
  rotateCamera: true
  rotateCameraBy: {
    direction: string
    triggerKey: string
  }
  zoomCamera: {
    action: string
    triggerKey: string
  }
  activeBuilding: string
  modelResetTriggerKey: string
}

export interface ThemeConfigInterface {
  font: string
  mainColour: string
  neutralColour: string
  secondaryColour: string
  tertiaryColour: string
  remoteTertiaryColour: string
  remoteBackground: BackgroundInterface
  remoteVisionBackground: BackgroundInterface
  remoteVisionLogo: AssetInterface
}

export interface GallerySettingsInterface {
  type: string
  intervalInSeconds: number
}

export interface User {
  name: string
  email: string
}

export interface Token {
  token: string
  expires_at: string
  _id: string
}

export interface RemoteInteractiveBuildingInterface {
  areaView: {
    useMatrix: boolean
  }
}

export interface AssetInterface {
  assetId?: string
  url?: string
}

export interface LogoInterface extends AssetInterface {
  visible: boolean
}

export interface BedConfigInterface {
  [key: string]: Array<number>
}

export interface BackgroundInterface extends AssetInterface {
  type: BackgroundType
  color?: string
}

export interface SessionListContent {
  sessionName: string
  sessionId: string
  projectId: string
  projectUUID: string
  projectName: string
  room?: string
}

export interface MarkerColourInterface {
  available: string
  sold: string
  area: string
  levels: string
}

export interface LocalAssetOptions {
  localImages: boolean
  localVideos: boolean
}

export interface ProjectIdentity {
  disconnect: boolean
  showClearCacheOption: boolean
  projectUUID: string
  projectId: string
  projectName: string
  projectLabel: string
  assetPort: string
  assetHost: string
  assetOptions: LocalAssetOptions
  sessionId: string
  sessionList: Array<SessionListContent>
  masterKey: string
  disconnectedSessions: Array<{
    sessionId: string
    projectId: string
  }>
  disableSoldProperty: boolean
  fullScreen: boolean
  lightUpBuildingModelOnFilter: boolean
  prices: PriceRange
  aspects: Array<string>
  unitTypes: Array<string>
  propertyConfig: PropertyConfigurationInterface
  houseAndLandConfig: HouseAndLandConfigurationInterface
  gallerySettings: GallerySettingsInterface
  hideFilter: boolean
  hideInfoPanelAspect: boolean
  hidePropertyInfoName: boolean
  hideSoldPrice: boolean
  idlePage: IdlePageInterface
  logo: LogoInterface
  longhand: boolean
  onlyShowPropertyGalleries: boolean
  propertyGalleryControlStyle: string
  propertyGalleryTab: PropertyGalleryTabInterface
  propertyPrefix: string
  showPrice: boolean
  showcaseActive: boolean
  sidePanelTitle: string
  markerColourSettings: MarkerColourInterface
  statusLabels: StatusLabels
  statusColors: StatusColors
  isSnaploaderActive: boolean
  isEnvisionVRActive: boolean
  hasThreeDViewer: boolean
  theme: ThemeConfigInterface
  navigationSettings: Array<MenuItemInterface>
  remoteInteractiveBuilding: RemoteInteractiveBuildingInterface
  type: ProjectType
  showcase3DIntegration: boolean
  disableCanvasZoom: boolean
}

export interface Level {
  level: string
  aspect: string
  floorPlan: string
  units: Array<Unit>
}

export interface Unit {
  name: string
  configuration: string
  status: string
  image: string
  price?: number
  gallery?: Array<string>
  coordinates?: {
    x: number
    y: number
  }
}

export interface Shortlist extends Unit {
  level: string
}

export interface GalleryItemInterface {
  id: string
  label?: string
  imageName?: string
  videoFileName?: string
  videoTitle?: string
  videoSource?: string
  config?: {
    isTitleCard?: boolean
    isTypeColor?: boolean
    blurred?: boolean
    staticUrl?: boolean
    noSpliceUrl?: boolean
  }
  title?: string
  imageSource: string
  assetType?: 'legacy' | 'new'
}

export interface GalleryInterface {
  label?: string
  items: Array<GalleryItemInterface>
}

export interface TabInterface {
  index: number
  label: string
}

export interface LotGallery {
  id: string
  src: string
  label: string
  type: FileType
  noSpliceUrl: boolean
  thumbnail?: string
  gradiant?: number
  staticUrl?: boolean
}

export interface GalleryControlInterface {
  activeTabIndex: number
  activeItemID: string
  transitionTime: number
  transitionType: string
  isPlaying: boolean
}

export interface PlayerControlInterface {
  isPlayerVisible: boolean
  isMuted: boolean
  volume: number
  playerState: string
  fullScreen: boolean
  scrubbedLength: number
  videoLengthInSeconds: number
  isBuffering: boolean
  scrubPositionInSeconds: number
  currentVideoPositionInSeconds: number
  currentlyPlaying: string
  videoSynchronizeTriggerKey: boolean
}

export interface InteractiveMapItemInterface {
  label?: string
  controlSrc: string
  groupId: string
  actionRoute: string
  visible: boolean
  categoryDefault: boolean
  baseImageOverride: string
}

export interface InteractiveMapListInterface {
  label?: string
  items: Array<InteractiveMapItemInterface>
}

export interface MenuItemInterface {
  key: string
  label: string
  active: boolean
  type?: 'main-menu' | 'footer-menu'
  order?: number
  route?: string
  relatedRoutes?: Array<string>
  routeViaFirebase?: boolean
  render?: (data: {
    menu: MenuItemInterface
    content?: ReactNode
    key?: string
    className?: string
    style?: React.CSSProperties
    tabIndex?: number
  }) => ReactNode
}

export interface FloorTabInterface {
  key: string
  label: string
}

export interface HorizontalScrollContainerRef {
  isDragging(): boolean
}

export interface PackageConfigurationInterface {
  floorplan?: string
  floorplanOption?: string
  facade?: string
  facadeColor?: string
  internalTheme?: string
  internalColorScheme?: string
}

interface BaseShortlistInterface {
  propertyId: string
  propertyName: string
}

export interface UnitShortlistInterface extends BaseShortlistInterface {
  references: {
    blockId: string | null
    levelId: string
  }
  type: ShortlistPropertyType.Unit
}

export interface LotShortlistInterface extends BaseShortlistInterface {
  configuration?: PackageConfigurationInterface
  references: {
    precinctId: string
    stageId: string
  }
  type: ShortlistPropertyType.Lot
}

export type ShortlistItemInterface =
  | UnitShortlistInterface
  | LotShortlistInterface

// TODO: Remove the following types after verifying since these are not being used anymore
export interface RouteInterface {
  label: string
  route: string
}
export interface SessionKey {
  key: string
  project: string
}
export interface MasterSessionKey {
  projectId: string
  key: string
  room: string
}
export interface VersionInterface {
  version: string
}
export interface Project {
  name: string
  id: string
  canonical_name: string
}
export interface RangeInterface {
  min: number
  max: number
}
export interface SessionMap {
  client: { remote: boolean }
  connected: boolean
  activeRoute: string
  previousRoute: string
  appIdleKey: string
  showcaseRefreshTriggerKey: string
  remoteRefreshTriggerKey: string
  clearCacheTriggerKey: string
  clearCacheKeyState: string
  sessionOwners: Array<{
    name: string
    email: string
    lastUpdate: string
  }>
  vision: {
    galleryControl: GalleryControlInterface
    galleryControlV2: GalleryControlInterfaceV2
    playerControl: PlayerControlInterface
    sessionModal: boolean
  }
  areaView: {
    triggerBuilding: boolean
  }
  design: {
    galleryControl: GalleryControlInterface
    galleryControlV2: GalleryControlInterfaceV2
  }
  brochure: {
    galleryControl: GalleryControlInterface
    galleryControlV2: GalleryControlInterfaceV2
  }
  location: {
    galleryControl: GalleryControlInterface
  }
  team: {
    galleryControl: GalleryControlInterface
  }
  floorGallery: {
    galleryControl: GalleryControlInterface
    playerControl: PlayerControlInterface
    galleryControlV2: GalleryControlInterfaceV2
    playerControlV2: PlayerControlInterfaceV2
  }
  propertyGallery: {
    galleryControlV2: GalleryControlInterfaceV2
    playerControlV2: PlayerControlInterfaceV2
  }
  propertyCompare: PropertyCompare
  building: {
    activeFacade: string
    activeLevel: string
    activeBlock: string
    activeUnit: string
    filterPopup: boolean
    unitInfoPopup: boolean
    externalLinkPopup: boolean
    generalExternalLinkPopup: boolean
    sidePanelFolded: boolean
    unitFilter: ExtendedUnitFilterOptions
    levelsShowLabels: boolean
    unitsShowLabels: boolean
    aspect: string
    showAvailable: boolean
    apply: boolean
  }
  houseAndLand: {
    activeStageId: string
    activePrecinctId: string
    activeLotId: string
    activePackageId: string
    activeFloorplanOptionId: string
    activeFacadeId: string
    activeFacadeColorId: string
    activeInternalThemeId: string
    activeInternalColorSchemeId: string
    lastActiveOption: {
      type: HouseAndLandPackageOptionType
      id: string
    }
    filterPopup: boolean
    lotInfoPopup: boolean
    externalLinkPopup: boolean
    generalExternalLinkPopup: boolean
    sidePanelFolded: boolean
    lotFilter: LotFilterInterface
    isDataLoaded: boolean
  }
  panoramic: {
    galleryControl: GalleryControlInterface
    pitch: number
    yaw: number
    zoom: number
    scene: string
    rotationSpeed: number
  }
  videoGallery: {
    galleryControl: GalleryControlInterface
    playerControl: PlayerControlInterface
    galleryControlV2: GalleryControlInterfaceV2
    playerControlV2: PlayerControlInterfaceV2
  }
  interactiveMap: Array<string>
  interactiveMapActiveCategory: {
    label: string
    baseImageOverride: string
  }
  externalView: number
  snaploader: SnaploaderInterface
  envisionVR: EnvisionVRInterface
  shortlist: {
    properties: ShortlistItemInterface[]
    selectedDocumentIds: string[]
    selectedAgentId: string
  }
  threeDView: {
    position: number[]
    activeModel: string
    activeScene: string
    canAnimate: boolean
    modelLoaded: boolean
    highLightTarget: string
    selectedLevelId: string
    showLevels: boolean
  }
}
export interface LotGalleryData {
  lot: string
  items: Array<LotGallery>
}

export enum PlayerState {
  Play = 'play',
  Pause = 'pause',
  Stop = 'stop',
}

export interface GalleryControlInterfaceV2 {
  activeTabIndex: number
  activeSlideIndex: number
  autoPlayState: boolean
  pinnedItemIndex: number | null
}

export interface PlayerControlInterfaceV2 {
  scrubbarTimerState: boolean
  playerState: PlayerState
  isBuffering: boolean
  loop: boolean
  seekToValueInSeconds: number | null
  muted: boolean
  volume: number
  videoLengthInSeconds: number
  currentVideoPositionInSeconds: number
  videoSyncKey: number | null
}

export enum BucketSource {
  Legacy = 'legacy',
  New = 'new',
}

export interface FloorGalleryItem {
  id: string
  src: string
  videoSource: string | undefined
  thumbnail: string | undefined
  fileType: FileType
  bucketType: BucketSource
  noSpliceUrl: boolean
}

export interface IdlePageInterface {
  active: boolean
  page: string
}

export interface EnvisionVRConfigurationInterface {
  active: boolean
  url: string
  zoom: number
  rotateCameraBy: number
}
export interface VendorsInterface {
  envisionVr: EnvisionVRConfigurationInterface
  snaploader: SnaploaderInterface
  threeDViewer: {
    active: string
    url: string
  }
}

export interface PropertyConfigurationInterface {
  bath?: Array<number>
  bed?: Array<number>
  car?: Array<number>
  study?: Array<number>
  powderRoom?: Array<number>
}

export interface PropertyGalleryTabInterface {
  active: boolean
  duration: number
}

export enum Status {
  UNINITIALIZED = 'uninitialized',
  PENDING = 'pending',
  FULFILLED = 'fulfilled',
  REJECTED = 'rejected',
}

export interface DisplayScreen {
  label: string
  masterKey: string
  currentProject: string
  sessionKeys: MasterSessionKey[]
}

export interface MultiScreen {
  active: boolean
  mirroredScreens: string[]
  displayScreens: DisplayScreen[]
}
